/**
 * User profile component - displays user information
 */

import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ChangePassword } from '@/components/auth/ChangePassword';
import { TwoFactorSettings } from '@/components/settings/TwoFactorSettings';
import { ActiveSessions } from '@/components/settings/ActiveSessions';
import { SupportAccessList } from '@/components/impersonation/SupportAccessList';
import { ConsentHistory } from '@/components/settings/ConsentHistory';
import { LanguageSelector } from './LanguageSelector';
import { TimezoneSelector } from './TimezoneSelector';
import { toast } from 'sonner';
import { useFormattedDate } from '../hooks/useUserTimezone';

interface UserProfileProps {
  onClose?: () => void;
  onLogout?: () => void;
}

export function UserProfile({ onClose, onLogout }: UserProfileProps) {
  const { user, logout: authLogout, preferences, updatePreferences } = useAuth();
  const { fmt } = useFormattedDate();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showTwoFactorSettings, setShowTwoFactorSettings] = useState(false);
  const [showActiveSessions, setShowActiveSessions] = useState(false);
  const [showSupportAccess, setShowSupportAccess] = useState(false);
  const [showConsentHistory, setShowConsentHistory] = useState(false);
  const [showLanguageTimezone, setShowLanguageTimezone] = useState(false);
  const [language, setLanguage] = useState(preferences?.language_code || 'en');
  const [timezone, setTimezone] = useState(preferences?.timezone || 'America/Los_Angeles');
  const [savingPreferences, setSavingPreferences] = useState(false);

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      authLogout();
    }
  };

  const handleSavePreferences = async () => {
    setSavingPreferences(true);
    try {
      await updatePreferences({
        language_code: language,
        timezone: timezone,
      });
      toast.success('Preferences saved', {
        description: 'Your language and timezone preferences have been updated.',
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Error saving preferences', {
        description: 'Failed to save your preferences. Please try again.',
      });
    } finally {
      setSavingPreferences(false);
    }
  };

  const hasPreferenceChanges = () => {
    return language !== preferences?.language_code || timezone !== preferences?.timezone;
  };

  if (!user) return null;

  const formatDate = (dateString: string) => {
    return fmt.dateTime(dateString);
  };

  const getRoleDisplayName = (role: string) => {
    const roleMap: Record<string, string> = {
      // New Phase 1 roles
      'case_manager': 'Case Manager',
      'community_provider': 'Community Provider (CBO)',
      'administrator': 'Administrator',
      // Legacy roles (for backwards compatibility)
      'provider': 'Healthcare Provider',
      'cbo_staff': 'CBO Staff',
      'admin': 'Administrator',
      'system': 'System User'
    };
    return roleMap[role] || role;
  };

  const formatPhoneNumber = (phone: string | undefined) => {
    if (!phone) return 'Not provided';
    return phone;
  };

  return (
    <div className="bg-white shadow-xl rounded-lg p-6 max-w-md">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-900">User Profile</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <div className="space-y-4">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-500 mb-1">Full Name</label>
          <p className="text-base text-gray-900 font-medium">{user.name}</p>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-500 mb-1">Email Address</label>
          <p className="text-base text-gray-900">{user.email}</p>
        </div>

        {/* Phone Number */}
        {user.phone_number && (
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Phone Number</label>
            <p className="text-base text-gray-900">{formatPhoneNumber(user.phone_number)}</p>
          </div>
        )}

        {/* Role */}
        <div>
          <label className="block text-sm font-medium text-gray-500 mb-1">Role</label>
          <p className="text-base text-gray-900">{getRoleDisplayName(user.role)}</p>
        </div>

        {/* Account Status - Always Active (green) for logged-in users */}
        <div>
          <label className="block text-sm font-medium text-gray-500 mb-1">Account Status</label>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Active
          </span>
        </div>

        {/* Email Verification Status */}
        {user.email_verified !== undefined && (
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Email Verified</label>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              user.email_verified
                ? 'bg-blue-100 text-blue-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {user.email_verified ? (
                <>
                  <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Verified
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Pending
                </>
              )}
            </span>
          </div>
        )}

        {/* Member Since */}
        <div>
          <label className="block text-sm font-medium text-gray-500 mb-1">Member Since</label>
          <p className="text-base text-gray-900">{formatDate(user.created_at)}</p>
        </div>

        {/* Last Login */}
        {user.last_login && (
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Last Login</label>
            <p className="text-base text-gray-900">{formatDate(user.last_login)}</p>
          </div>
        )}
      </div>

      {/* Change Password Section */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <button
          onClick={() => setShowChangePassword(!showChangePassword)}
          className="w-full flex justify-center items-center py-2.5 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors mb-3"
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
          {showChangePassword ? 'Hide' : 'Change Password'}
        </button>

        {showChangePassword && (
          <div className="mb-4">
            <ChangePassword />
          </div>
        )}
      </div>

      {/* Two-Factor Authentication Section */}
      <div className="mt-4 pt-6 border-t border-gray-200">
        <button
          onClick={() => setShowTwoFactorSettings(!showTwoFactorSettings)}
          className="w-full flex justify-center items-center py-2.5 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors mb-3"
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          {showTwoFactorSettings ? 'Hide' : 'Two-Factor Authentication'}
        </button>

        {showTwoFactorSettings && (
          <div className="mb-4">
            <TwoFactorSettings />
          </div>
        )}
      </div>

      {/* Active Devices Section */}
      <div className="mt-4 pt-6 border-t border-gray-200">
        <button
          onClick={() => setShowActiveSessions(!showActiveSessions)}
          className="w-full flex justify-center items-center py-2.5 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors mb-3"
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17h4.5m-9-3h13.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H5.25A1.5 1.5 0 003.75 6v6.5A1.5 1.5 0 005.25 14z" />
          </svg>
          {showActiveSessions ? 'Hide' : 'Active Devices'}
        </button>

        {showActiveSessions && (
          <div className="mb-4">
            <ActiveSessions />
          </div>
        )}
      </div>

      {/* Support access (TRUS-747): when a super admin viewed the app as you */}
      <div className="mt-4 pt-6 border-t border-gray-200">
        <button
          onClick={() => setShowSupportAccess(!showSupportAccess)}
          className="w-full flex justify-center items-center py-2.5 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors mb-3"
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.25 12s3.75-7.5 9.75-7.5S21.75 12 21.75 12s-3.75 7.5-9.75 7.5S2.25 12 2.25 12z" />
            <circle cx="12" cy="12" r="3" strokeWidth={2} />
          </svg>
          {showSupportAccess ? 'Hide' : 'Support Access'}
        </button>

        {showSupportAccess && (
          <div className="mb-4">
            <SupportAccessList />
          </div>
        )}
      </div>

      {/* Language & Timezone Preferences Section */}
      <div className="mt-4 pt-6 border-t border-gray-200">
        <button
          onClick={() => setShowLanguageTimezone(!showLanguageTimezone)}
          className="w-full flex justify-center items-center py-2.5 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors mb-3"
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
          </svg>
          {showLanguageTimezone ? 'Hide' : 'Language & Timezone Preferences'}
        </button>

        {showLanguageTimezone && (
          <div className="mb-4 space-y-4">
            <div>
              <LanguageSelector
                value={language}
                onChange={setLanguage}
                showLabel={true}
              />
            </div>

            <div>
              <TimezoneSelector
                value={timezone}
                onChange={setTimezone}
                showLabel={true}
              />
            </div>

            {hasPreferenceChanges() && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm text-yellow-800">
                  You have unsaved changes
                </p>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => {
                  setLanguage(preferences?.language_code || 'en');
                  setTimezone(preferences?.timezone || 'America/Los_Angeles');
                }}
                disabled={!hasPreferenceChanges() || savingPreferences}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePreferences}
                disabled={!hasPreferenceChanges() || savingPreferences}
                className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-v2accent hover:bg-v2accent/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {savingPreferences ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Consent & Signature History Section */}
      <div className="mt-4 pt-6 border-t border-gray-200">
        <button
          onClick={() => setShowConsentHistory(!showConsentHistory)}
          className="w-full flex justify-center items-center py-2.5 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors mb-3"
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          {showConsentHistory ? 'Hide' : 'Consent & Signature History'}
        </button>

        {showConsentHistory && (
          <div className="mb-4">
            <ConsentHistory />
          </div>
        )}
      </div>

      {/* Logout Button */}
      <div className="mt-4 pt-6 border-t border-gray-200">
        <button
          onClick={handleLogout}
          className="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Sign Out
        </button>
      </div>
    </div>
  );
}
